import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Activity as ActivityIcon, Search, FileText, GitBranch, Users, ClipboardCheck,
  Layers, Send, Settings2, Building2, ArrowRight,
} from "lucide-react";
import { ClaudeBadge } from "@/components/claude/ClaudeBadge";

const RANGES = [
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "quarter", label: "This quarter" },
  { key: "all", label: "All time" },
];

const CAT_ICON = {
  bids: Send, leads: Building2, estimating: Layers, revisions: GitBranch,
  proposals: FileText, outreach: Users, approvals: ClipboardCheck, admin: Settings2,
};
const CAT_TONE = {
  bids: "bg-[#003E87]/10 text-[#003E87]", leads: "bg-amber-50 text-amber-700",
  estimating: "bg-indigo-50 text-indigo-700", revisions: "bg-violet-50 text-violet-700",
  proposals: "bg-sky-50 text-sky-700", outreach: "bg-emerald-50 text-emerald-700",
  approvals: "bg-rose-50 text-rose-700", admin: "bg-slate-100 text-slate-600",
};

function rangeDates(key) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const to = iso(today);
  if (key === "all") return { from: "2000-01-01", to };
  if (key === "month") return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to };
  if (key === "quarter") {
    const q = Math.floor(today.getMonth() / 3) * 3;
    return { from: iso(new Date(today.getFullYear(), q, 1)), to };
  }
  const d = new Date(today);
  const dow = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - dow);
  return { from: iso(d), to };
}

const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const dayKey = (iso) => (iso || "").slice(0, 10);
const dayLabel = (k) => {
  try {
    const t = new Date().toISOString().slice(0, 10);
    const y = new Date(Date.now() - 864e5).toISOString().slice(0, 10);
    if (k === t) return "Today";
    if (k === y) return "Yesterday";
    return new Date(k + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
  } catch { return k; }
};
const timeLabel = (iso) => {
  try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

export default function Activity() {
  const navigate = useNavigate();
  const [range, setRange] = useState("week");
  const [person, setPerson] = useState("all");
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], people: [], categories: [], can_view_all: false });
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const { from, to } = rangeDates(range);
    const params = { from, to };
    if (person !== "all") params.user_id = person;
    if (cat !== "all") params.category = cat;
    if (q.trim()) params.q = q.trim();
    api.get("/activity/feed", { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], people: [], categories: [], can_view_all: false }))
      .finally(() => setLoading(false));
  };
  // reload on filter change (debounce search)
  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [range, person, cat, q]);

  const grouped = useMemo(() => {
    const g = [];
    let cur = null;
    for (const it of data.items) {
      const k = dayKey(it.at);
      if (!cur || cur.key !== k) { cur = { key: k, items: [] }; g.push(cur); }
      cur.items.push(it);
    }
    return g;
  }, [data.items]);

  return (
    <div className="px-6 sm:px-8 py-8 max-w-[1800px] w-full mx-auto" data-testid="activity-page">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2">
          <ActivityIcon className="w-5 h-5 text-[#003E87]" /> Activity
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Who did what, and who moved each bid to the next stage — {data.can_view_all ? "across the whole company." : "for you and your direct reports."}
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {RANGES.map((r) => (
            <button key={r.key} data-testid={`activity-range-${r.key}`} onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${range === r.key ? "bg-[#003E87] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {r.label}
            </button>
          ))}
        </div>
        {data.people.length > 1 && (
          <Select value={person} onValueChange={setPerson}>
            <SelectTrigger className="w-52" data-testid="activity-person-select"><SelectValue placeholder="Everyone" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone{data.can_view_all ? "" : " (you + reports)"}</SelectItem>
              {data.people.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input data-testid="activity-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search actions, people, projects…" className="pl-8" />
        </div>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-1.5 mb-5">
        <button data-testid="activity-cat-all" onClick={() => setCat("all")}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${cat === "all" ? "bg-slate-900 text-white border-slate-900" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
          All
        </button>
        {data.categories.map((c) => (
          <button key={c.key} data-testid={`activity-cat-${c.key}`} onClick={() => setCat(c.key)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${cat === c.key ? "bg-slate-900 text-white border-slate-900" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-16 text-center" data-testid="activity-loading">Loading activity…</div>
      ) : grouped.length === 0 ? (
        <Card className="p-12 text-center" data-testid="activity-empty">
          <ActivityIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <div className="text-sm font-medium text-slate-600">No activity in this range</div>
          <div className="text-xs text-slate-400 mt-1">Try a wider time window or clear the filters.</div>
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.map((day) => (
            <div key={day.key} data-testid={`activity-day-${day.key}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{dayLabel(day.key)}</div>
              <Card className="divide-y divide-slate-100">
                {day.items.map((it) => {
                  const Icon = CAT_ICON[it.category] || ActivityIcon;
                  return (
                    <div key={it.activity_id} data-testid={`activity-row-${it.activity_id}`}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50/60 transition-colors">
                      <div className={`w-8 h-8 rounded-full grid place-items-center text-[11px] font-bold shrink-0 ${CAT_TONE[it.category] || "bg-slate-100 text-slate-600"}`}
                        title={it.user_name}>
                        {initials(it.user_name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-800">
                          <span className="font-semibold text-slate-900">{it.user_name}</span>{" "}
                          {it.via === "claude" && <><ClaudeBadge testid={`activity-claude-${it.activity_id}`} />{" "}</>}
                          <span className="text-slate-500">· {it.title}</span>
                        </div>
                        <div className="text-sm text-slate-600 truncate">{it.detail}</div>
                        {it.project && (
                          <button onClick={() => it.link && navigate(it.link)} data-testid={`activity-project-${it.activity_id}`}
                            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[#003E87] hover:underline">
                            <Layers className="w-3 h-3" /> {it.project}{it.client ? ` · ${it.client}` : ""} <ArrowRight className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`hidden sm:inline text-[10px] font-medium px-1.5 py-0.5 rounded ${CAT_TONE[it.category] || "bg-slate-100 text-slate-600"}`}>
                          <Icon className="w-3 h-3 inline -mt-0.5 mr-0.5" />{it.category}
                        </span>
                        <span className="text-[11px] text-slate-400 tabular-nums w-14 text-right">{timeLabel(it.at)}</span>
                      </div>
                    </div>
                  );
                })}
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
